<template>
  <article class="receipt-electronic">
    <div class="receipt-electronic__container">
      <header>
        <h1>{{ t('title.e-receipt_issuance') }}</h1>
      </header>

      <section class="receipt-electronic__info">
        <div class="receipt-electronic__guide">
          <i18n-t keypath="info.e-receipt_guide1" tag="p" class="receipt-electronic__guide1">
            <template #mobile_number>
              <span>{{ t('label.mobile_number') }}</span>
            </template>
          </i18n-t>

          <div class="receipt-electronic__guide-group">
            <div class="receipt-electronic__guide2">
              <span>{{ t('info.e-receipt_guide2') }}</span>
            </div>
          </div>
        </div>

        <figure class="receipt-electronic__figure">
          <img src="@/assets/images/img-electronic-receipt.jpg" alt="전자영수증발급 이미지" />
        </figure>
      </section>

      <main>
        <BaseKeypadPhone v-model="mobileNumber" />
      </main>

      <footer>
        <BaseButtonGroup>
          <BaseButton class="outline" :flex="1" @mouseup="handleCancelClick" @touchend.prevent="handleCancelClick">
            <template #icon>
              <svg xmlns="http://www.w3.org/2000/svg" width="29.442" height="25.247" viewBox="0 0 29.442 25.247">
                <path
                  id="arrow-left-solid"
                  d="M.641,75.063a2.106,2.106,0,0,0,0,2.977L11.157,88.556a2.105,2.105,0,0,0,2.977-2.977L7.2,78.652H27.364a2.1,2.1,0,1,0,0-4.206H7.207l6.921-6.927a2.105,2.105,0,1,0-2.977-2.977L.635,75.057Z"
                  transform="translate(-0.025 -63.925)"
                />
              </svg>
            </template>
            {{ t('button.prev') }}
          </BaseButton>
          <BaseButton :flex="2" @mouseup="handleConfirmClick" @touchend.prevent="handleConfirmClick">
            <template #icon>
              <img src="@/assets/images/icon-check-white.svg" alt="확인 아이콘" />
            </template>
            {{ t('button.confirm') }}
          </BaseButton>
        </BaseButtonGroup>
      </footer>
    </div>
  </article>
</template>

<script lang="ts" setup>
  import * as Android from '@/api/android';
  import BaseButton from '@/components/base/BaseButton.vue';
  import BaseButtonGroup from '@/components/base/BaseButtonGroup.vue';
  import OrderComplete from '@/components/OrderComplete.vue';
  import UserError from '@/error';
  import { useCommonStore } from '@/stores/common';
  import { useModalStore } from '@/stores/modal';
  import { ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import BaseKeypadPhone from './base/BaseKeypadPhone.vue';

  const props = defineProps<{
    /**
     * 주문내역에서 영수증 요청 버튼을 통해 모달을 열었는지 구분.
     * - 주문내역에서 요청 했다면 영수증 요청 후 모달 닫기.
     * - 선결제 완료 후 영수증 요청한다면 주문 완료 모달 열기.
     */
    fromOrderHistory: boolean;
  }>();
  const emits = defineEmits(['close']);

  const { t } = useI18n();
  const commonStore = useCommonStore();
  const modalStore = useModalStore();

  const mobileNumber = ref('010');

  /** 취소 버튼 클릭 */
  const handleCancelClick = () => modalStore.goBackFullScreenModal();

  /** 확인 버튼 클릭 */
  const handleConfirmClick = async () => {
    if (mobileNumber.value.length < 11) throw new UserError(t('error.not_valid_mobile_number'));

    const loadingId = commonStore.showLoading();
    try {
      // 전자 영수증 발급 요청
      const response = await Android.requestEReceiptAsync(mobileNumber.value);
      // 전자 영수증 발급 요청 성공 여부 검사
      if (response.RET_CODE !== 0) throw new UserError(response.RET_MSG);

      await commonStore.showDialog({ type: 'info', message: t('info.electronic_receipt_complete') });

      if (props.fromOrderHistory) {
        emits('close');
      } else {
        modalStore.openFullScreenModal(OrderComplete, { hasPayment: true });
      }
    } finally {
      commonStore.hideLoading(loadingId);
    }
  };
</script>

<style lang="scss" scoped>
  @use '@/assets/scss/variables' as vars;

  .receipt-electronic {
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100vh;
    .receipt-electronic__container {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-template-rows: 2.8358vw 1fr 6.7164vw;
      justify-content: center;
      gap: 1.4925vw;
      padding: 2.9851vw;
      @media (max-width: vars.$sm) {
        grid-template-columns: 1fr;
        grid-template-rows: 24px auto auto 60px;
        height: 100%;
      }

      header {
        grid-row: 1 / 2;
        h1 {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          height: 2.8358vw;
          padding: 0 1.4925vw;
          border-radius: 1.4179vw;
          background: #000000;
          color: #fff;
          font-size: 1.4925vw;
          letter-spacing: -0.0746vw;
          @media (max-width: vars.$sm) {
            height: 24px;
            border-radius: 12px;
            font-size: 10px;
          }
        }
      }

      .receipt-electronic__info {
        grid-row: 2 / 4;
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 0.7463vw;
        @media (max-width: vars.$sm) {
          grid-row: 2 / 3;
        }
        .receipt-electronic__guide {
          display: flex;
          flex-direction: column;
          gap: 1.4925vw;
          justify-content: center;
          .receipt-electronic__guide1 {
            font-size: 2.9851vw;
            font-weight: 400;
            line-height: 4.4776vw;
            margin-bottom: 0.7463vw;
            @media (max-width: vars.$md) {
              font-size: 22px;
              line-height: 33px;
            }
            span {
              font-weight: 700;
              color: var(--point-color-secondary);
            }
          }

          .receipt-electronic__guide2 {
            display: inline-flex;
            align-items: center;
            height: 2.2388vw;
            padding: 0 1.4925vw 0 2.0896vw;
            border: 0.1493vw solid var(--point-color-secondary);
            border-radius: 1.1194vw;
            font-size: 1.0448vw;
            letter-spacing: -0.0313vw;
            color: var(--point-color-secondary);
            background: url('@/assets/images/icon-info-red.svg') no-repeat 0.7463vw 0.5224vw;
            background-size: 1.0448vw;
            word-break: keep-all;
            @media (max-width: vars.$md) {
              height: 20px;
              border-radius: 10px;
              font-size: 8px;
              padding: 0 12px;
              background-position: 2px center;
              background-size: 10px;
            }
          }
        }

        .receipt-electronic__figure {
          padding-top: 1.4925vw;
          @media (max-width: vars.$sm) {
            text-align: center;
          }
          img {
            width: 37.3134vw;
            object-fit: contain;
            @media (max-width: vars.$sm) {
              width: 240px;
            }
          }
        }
      }

      main {
        grid-row: 2 / 3;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        @media (max-width: vars.$sm) {
          grid-row: 3 / 4;
        }
        .receipt-electronic__input {
          width: 100%;
          text-align: center;
          font-size: 2.9851vw;
          font-weight: 700;
          line-height: 4.4776vw;
          border-top: none;
          border-left: none;
          border-right: none;
          border-bottom: 0.2239vw solid var(--border-color-secondary);
          padding-bottom: 1.0448vw;
          outline: 0;
          margin-bottom: 2.6119vw;
          @media (max-width: vars.$md) {
            font-size: 20px;
            border-bottom-width: 1px;
          }
        }
      }

      footer {
        grid-row: 3 / 4;
        @media (max-width: vars.$sm) {
          grid-row: 4 / 5;
        }
      }
    }
  }
</style>
